import { isSignupSource, type SignupSource } from '@floorpoll/shared';

/**
 * Which channel a signup link on this page should claim.
 *
 * **Carried in the URL, never in browser storage.** The Privacy Policy
 * enumerates local storage as five strictly functional things and shows no
 * cookie banner on the strength of that list being complete; a sixth entry for
 * marketing attribution would be both a policy edit and a `TERMS_VERSION`
 * bump, and a bump now pauses every lifecycle email until each person
 * re-accepts. A query parameter costs none of that. It also survives a reload,
 * which an in-memory value would not.
 *
 * Nothing here is personal: a channel label describes the page someone came
 * from, not the person. That is the only reason it is allowed in a URL at all.
 */

/**
 * An explicit `?via=` beats everything — that is how paid search labels its
 * own clicks, and an ad landing on a comparison page must not be recorded as
 * organic traffic to that page.
 *
 * Unrecognised values are dropped rather than forwarded. The API folds
 * anything it does not know into `other` regardless, so passing junk along
 * only widens what a stranger can write into the query string.
 */
export function viaFromSearch(search: string): SignupSource | undefined {
  const explicit = new URLSearchParams(search).get('via');
  return explicit && isSignupSource(explicit) ? explicit : undefined;
}

/**
 * The channel a public page represents, from its own path.
 *
 * Deriving this beats hardcoding a slug into each page's CTA: the marketing
 * routes already know their own paths, and a new comparison page picks up
 * attribution by existing rather than by someone remembering to label its
 * button.
 *
 * The landing page and every console route return undefined — they are not a
 * channel, and a signup from one is `direct`.
 */
export function viaFromPath(pathname: string): SignupSource | undefined {
  const trimmed = pathname.replace(/\/+$/, '');
  if (trimmed === '' || trimmed === '/') return undefined;

  const segments = trimmed.split('/').filter(Boolean);
  // `/j` and `/j/ABC234` are both the join screen: the code on it names a
  // room, not a channel, and the slug for the channel was seeded as `join`.
  // Without this the first segment is `j`, which is no slug, and the one
  // channel whose reach grows with usage is counted as `direct`.
  if (segments[0] === 'j') return 'join';
  // `/use-cases/town-halls` is labelled `town-halls`: the prefix is identical
  // on every use-case page, so it carries no signal worth storing.
  const candidate = segments[0] === 'use-cases' ? segments[1] : segments[0];

  return candidate && isSignupSource(candidate) ? candidate : undefined;
}

/**
 * `/signup`, with the channel attached when there is one to attach.
 *
 * Returns the bare path for `direct` rather than `?via=direct`, so a URL only
 * ever carries a parameter that means something. The API reads an absent
 * `via` as `direct` anyway.
 */
export function signupHref(pathname: string, search: string): string {
  const via = viaFromSearch(search) ?? viaFromPath(pathname);
  return via ? `/signup?via=${encodeURIComponent(via)}` : '/signup';
}
